import type { VehiclePhotoPrompt, InspectionPhoto, InspectionCategory, InspectionType } from '@/types';
import { analyzePhoto } from './aiAnalysis';
import { authFetch } from '@/utils/authFetch';
import { VEHICLE_PHOTO_PROMPTS } from '@/utils/vehiclePhotoPrompts';
import { compressImageForUpload } from '@/utils/imageCompression';

// ============================================================
// Maps each photo prompt to the check IDs it auto-updates
// ============================================================
export const PHOTO_TO_CHECKS: Record<VehiclePhotoPrompt, string[]> = {
  'front-body': ['ext-body', 'ext-paint', 'lt-headlights', 'lt-turn', 'lt-reflectors', 'tr-lf', 'tr-lr', 'tr-lugs'],
  'left-side-body': ['ext-body', 'ext-paint', 'ext-door-locks', 'eq-racks'],
  'right-side-body': ['ext-body', 'ext-paint', 'ext-door-locks', 'eq-racks', 'tr-rf', 'tr-rr', 'tr-lugs'],
  'rear-body': ['ext-body', 'lt-tail', 'lt-brake', 'lt-reverse', 'lt-turn', 'lt-reflectors', 'eq-fiber', 'eq-cargo'],
  'left-mirror': ['ext-mirrors'],
  'right-mirror': ['ext-mirrors'],
  'windshield': ['ext-windows', 'ext-wipers'],
  'front-left-tire': ['tr-lf', 'tr-lugs'],
  'front-right-tire': ['tr-rf', 'tr-lugs'],
  'rear-left-tire': ['tr-lr', 'tr-lugs'],
  'rear-right-tire': ['tr-rr', 'tr-lugs'],
  'spare-tire': ['tr-spare'],
  'interior-cab-driver': ['int-seat', 'int-clean', 'int-reg'],
  'interior-cab-passenger': ['int-seat', 'int-clean'],
  'interior-cargo': ['eq-cargo', 'eq-tools', 'eq-ladder', 'eq-fiber'],
  'dashboard-gauges': ['int-gauges'],
  'engine-oil': ['fl-oil'],
  'coolant-reservoir': ['fl-coolant'],
  'brake-fluid': ['fl-brake'],
  'transmission-fluid': ['fl-trans'],
  'washer-fluid': ['fl-washer'],
  'underside': ['fl-leaks'],
  'belts-hoses': ['fl-belts'],
  'registration-cards': ['int-reg'],
  'fire-extinguisher': ['sf-extinguisher'],
  'warning-triangles': ['sf-triangles'],
  'hi-vis-vest': ['sf-vest'],
  'traffic-cones': ['sf-cones'],
  'first-aid-kit': ['sf-firstaid'],
  'ladder-rack': ['eq-racks', 'eq-ladder'],
  'close-up-damage-1': ['ext-body', 'ext-paint'],
  'close-up-damage-2': ['ext-body', 'ext-paint'],
  'other-feature': [],
};

export function getChecksForPhoto(promptId: VehiclePhotoPrompt, inspectionType?: InspectionType): string[] {
  const checks = PHOTO_TO_CHECKS[promptId] ?? [];
  // On monthly inspections there's a dedicated ladder-rack photo — the ladder
  // check should only be resolved from that, not from the general cargo shot.
  if (promptId === 'interior-cargo' && inspectionType === 'monthly') {
    return checks.filter(id => id !== 'eq-ladder');
  }
  return checks;
}

// Photos where a ladder may or may not be present — most vehicles in this
// fleet don't carry one, so its absence must never fail the ladder check.
const LADDER_SOURCE_PROMPTS = new Set<VehiclePhotoPrompt>(['interior-cargo', 'ladder-rack']);

export function determineStatusFromAnalysis(
  analysis: InspectionPhoto['aiAnalysis']
): 'pass' | 'fail' | null {
  if (!analysis) return null;
  if (!analysis.hasDamage) return 'pass';
  if (['none', 'minor', 'moderate'].includes(analysis.severity)) return 'pass';
  if (analysis.severity === 'severe' && analysis.confidence >= 0.85) return 'fail';
  return 'pass';
}

/**
 * Builds a human-readable AI reasoning string for a failed check,
 * based on what the AI detected in the photo.
 */
function buildAIReasoning(
  analysis: InspectionPhoto['aiAnalysis'],
  promptLabel: string,
  checkLabel: string
): string {
  if (!analysis) return '';

  const wrongSubjectIssue = analysis.detectedIssues.find(i =>
    i.startsWith('WRONG SUBJECT:') || i.startsWith('UNUSABLE PHOTO:')
  );
  if (wrongSubjectIssue) {
    return `AI flagged this check based on photo of "${promptLabel}": ${wrongSubjectIssue}`;
  }

  const parts: string[] = [];
  parts.push(`AI analysis of the "${promptLabel}" photo detected: ${analysis.description}`);
  if (analysis.detectedIssues.length > 0) {
    parts.push(`Issues found: ${analysis.detectedIssues.join('; ')}`);
  }
  parts.push(`Severity: ${analysis.severity} (${Math.round(analysis.confidence * 100)}% confidence)`);
  return parts.join(' — ');
}

/**
 * Works out which checks a photo's AI analysis should update.
 *
 * The ladder check (eq-ladder) is graded independently of the photo's
 * overall verdict: most vehicles on this fleet don't carry a ladder, so its
 * absence must never fail the check, and an unrelated issue elsewhere in
 * the same photo (e.g. a loose chemical container) must not cascade into
 * failing it either. It only fails when the AI specifically calls out an
 * "UNSECURED LADDER:" issue; otherwise it passes.
 */
export function computeCheckUpdates(
  promptId: VehiclePhotoPrompt,
  analysis: InspectionPhoto['aiAnalysis'],
  categories: InspectionCategory[],
  inspectionType?: InspectionType,
): { categoryId: string; checkId: string; status: 'pass' | 'fail'; aiReasoning?: string }[] {
  const checkIds = getChecksForPhoto(promptId, inspectionType);
  const status = determineStatusFromAnalysis(analysis);
  const updatedChecks: { categoryId: string; checkId: string; status: 'pass' | 'fail'; aiReasoning?: string }[] = [];
  if (!status) return updatedChecks;

  const promptConfig = VEHICLE_PHOTO_PROMPTS.find(p => p.id === promptId);
  const promptLabel = promptConfig?.label ?? promptId;
  const ladderIssue = analysis?.detectedIssues.find(i => i.startsWith('UNSECURED LADDER:'));

  for (const cat of categories) {
    for (const check of cat.checks) {
      if (!checkIds.includes(check.id)) continue;

      if (check.id === 'eq-ladder' && LADDER_SOURCE_PROMPTS.has(promptId)) {
        const ladderStatus: 'pass' | 'fail' = ladderIssue ? 'fail' : 'pass';
        const shouldUpdate =
          check.status === null ||
          (ladderStatus === 'fail' && check.status === 'pass') ||
          (ladderStatus === 'pass' && check.status === 'fail');
        if (shouldUpdate) {
          updatedChecks.push({
            categoryId: cat.id,
            checkId: check.id,
            status: ladderStatus,
            aiReasoning: ladderIssue ? buildAIReasoning(analysis, promptLabel, check.label) : undefined,
          });
        }
        continue;
      }

      const shouldUpdate =
        check.status === null ||
        (status === 'fail' && check.status === 'pass') ||
        (status === 'pass' && check.status === 'fail');

      if (shouldUpdate) {
        const aiReasoning = status === 'fail' && analysis
          ? buildAIReasoning(analysis, promptLabel, check.label)
          : undefined;
        updatedChecks.push({ categoryId: cat.id, checkId: check.id, status, aiReasoning });
      }
    }
  }

  return updatedChecks;
}

/**
 * Returns true if an error is a network/connectivity failure
 * (as opposed to a server error, auth error, etc.)
 */
function isNetworkError(err: any): boolean {
  return (
    err instanceof TypeError ||
    err?.message?.includes('Failed to fetch') ||
    err?.message?.includes('NetworkError') ||
    err?.message?.includes('Network request failed') ||
    err?.name === 'TypeError'
  );
}

export async function applyPhotoToChecks(
  file: File,
  promptId: VehiclePhotoPrompt,
  categories: InspectionCategory[],
  inspectionType?: InspectionType
): Promise<{
  photo: InspectionPhoto;
  updatedChecks: { categoryId: string; checkId: string; status: 'pass' | 'fail'; aiReasoning?: string }[];
  pendingUpload?: boolean;
}> {
  const promptConfig = VEHICLE_PHOTO_PROMPTS.find(p => p.id === promptId);
  const promptLabel = promptConfig?.label ?? promptId;

  // Size check before attempting upload
  console.log('[Upload] File size:', `${(file.size / 1024 / 1024).toFixed(2)} MB`);
  if (file.size > 40 * 1024 * 1024) {
    throw new Error(`Photo is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Please try a lower resolution photo.`);
  }

  const formData = new FormData();
  // Compress and convert to JPEG before upload — handles HEIC from iPhones
  // and reduces file size for faster uploads on mobile
  const compressedFile = await compressImageForUpload(file);
  formData.append('photo', compressedFile, 'photo.jpg');
  formData.append('checkId', promptId);

  // ── Attempt upload ──────────────────────────────────────
  let uploadedUrl: string;

  try {
    const uploadRes = await authFetch('/api/inspections/temp/photos', {
      method: 'POST',
      body: formData,
    });

    if (!uploadRes.ok) {
      let errorDetail = `Server returned ${uploadRes.status}`;
      try {
        const errBody = await uploadRes.json();
        errorDetail = errBody.error ?? errorDetail;
      } catch {
        try { errorDetail = await uploadRes.text(); } catch {}
      }
      console.error('[Upload] Photo upload failed:', uploadRes.status, errorDetail);
      throw new Error(`Photo upload failed (${uploadRes.status}): ${errorDetail}`);
    }

    const { url } = await uploadRes.json();
    uploadedUrl = url;

  } catch (uploadErr: any) {
    // If it's a network/connectivity error (weak signal, dropped connection, etc.)
    // fall back to offline storage rather than failing the user
    if (isNetworkError(uploadErr)) {
      console.warn('[Upload] Network error — saving locally for sync when reconnected:', uploadErr.message);
      const blobUri = URL.createObjectURL(file);
      const photo: InspectionPhoto = {
        id: `photo-${Date.now()}`,
        uri: blobUri,
        timestamp: new Date().toISOString(),
        pendingUpload: true,
        pendingFile: file,
      };
      return { photo, updatedChecks: [], pendingUpload: true };
    }

    // Non-network error (auth, file size, server error) — rethrow with context
    throw uploadErr;
  }

  // ── Upload succeeded — run AI analysis ─────────────────
  const photo: InspectionPhoto = {
    id: `photo-${Date.now()}`,
    uri: uploadedUrl,
    timestamp: new Date().toISOString(),
  };

  try {
    const analysis = await analyzePhoto(file, promptId, promptLabel);
    photo.aiAnalysis = analysis;
  } catch (analysisErr: any) {
    // AI analysis failing should not block the photo from being saved
    console.warn('[Upload] AI analysis failed, continuing without it:', analysisErr?.message);
  }

  const updatedChecks = computeCheckUpdates(promptId, photo.aiAnalysis, categories, inspectionType);

  return { photo, updatedChecks };
}